import { useEffect, useRef, useState } from 'react';
import type { Brand } from '../lib/api';
import { takeFormat } from '../lib/prompter';
import { cameraError, cameraName, recorderStream, soundCarrier, takeNameFor, uploadTake, type CameraInfo } from '../lib/capture';

// ---------------------------------------------------------------------------
// The camera rig on the Sources screen.
//
// Every camera the browser can see, each with a live preview and its own
// Capture button, and one button that rolls them all. A take lands in Sources
// as a stored camera source — probed, normalised, transcribed like any take —
// and from there it is dragged onto a scene of the timeline, or added to a
// video from its row.
//
// Nothing opens until asked. A permission prompt the moment Sources loads,
// for someone who came to upload a tape, is the wrong first impression; so
// the rig is a closed panel with one button that asks.
// ---------------------------------------------------------------------------

interface Cam extends CameraInfo {
  stream: MediaStream | null;
  error: string | null;
}

export function CameraCapture({ brand, onCaptured, onError }: {
  brand: Brand | null;
  /** A take has been uploaded and sent to ingest; the library should reload. */
  onCaptured: () => void;
  onError: (m: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [cams, setCams] = useState<Cam[]>([]);
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [mic, setMic] = useState<string>('');
  const micStream = useRef<MediaStream | null>(null);
  const [speaks, setSpeaks] = useState<string | null>(null);       // the camera the microphone rides with
  const [label, setLabel] = useState('');
  const [count, setCount] = useState(1);
  const [rolling, setRolling] = useState<string[]>([]);
  const [uploading, setUploading] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const recorders = useRef(new Map<string, { rec: MediaRecorder; chunks: Blob[] }>());
  const startedAt = useRef(0);
  const videos = useRef(new Map<string, HTMLVideoElement>());
  const timer = useRef<number | null>(null);

  function stopTracks(s: MediaStream | null) { s?.getTracks().forEach((t) => t.stop()); }

  async function openMic(deviceId: string) {
    stopTracks(micStream.current); micStream.current = null;
    try {
      micStream.current = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, ...(deviceId ? { deviceId: { exact: deviceId } } : {}) },
      });
    } catch (e) { onError(`the microphone did not open: ${(e as Error).message}`); }
  }

  /** Ask once, then open every camera there is. One failing camera does not close the others. */
  async function openAll() {
    setOpen(true);
    let all: MediaDeviceInfo[];
    try {
      // Labels are withheld until a permission has been granted; a short
      // first open earns them and is closed at once.
      const probe = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      stopTracks(probe);
      all = await navigator.mediaDevices.enumerateDevices();
    } catch (e) { onError(cameraError(e)); setOpen(false); return; }
    const found = all.filter((d) => d.kind === 'videoinput');
    setMics(all.filter((d) => d.kind === 'audioinput'));
    const next: Cam[] = [];
    for (const d of found) {
      // In turn, not at once: some capture drivers refuse a second open
      // while the first is still negotiating.
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: d.deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false,
        });
        next.push({ deviceId: d.deviceId, label: d.label, stream, error: null });
      } catch (e) {
        next.push({ deviceId: d.deviceId, label: d.label, stream: null, error: cameraError(e) });
      }
    }
    setCams(next);
    setSpeaks((s) => s ?? next.find((c) => c.stream)?.deviceId ?? null);
    await openMic(mic);
    if (!found.length) onError('no camera was found. Plug one in, then open the cameras again.');
  }

  function closeAll() {
    for (const id of [...recorders.current.keys()]) stopOne(id);
    cams.forEach((c) => stopTracks(c.stream));
    stopTracks(micStream.current); micStream.current = null;
    setCams([]); setRolling([]); setOpen(false);
  }

  // The previews follow the streams; the elements are collected by ref as they mount.
  useEffect(() => {
    for (const c of cams) {
      const v = videos.current.get(c.deviceId);
      if (v && c.stream && v.srcObject !== c.stream) { v.srcObject = c.stream; void v.play().catch(() => undefined); }
    }
  }, [cams]);

  useEffect(() => () => {
    // Unmount: leave no camera light on.
    recorders.current.forEach(({ rec }) => { if (rec.state !== 'inactive') rec.stop(); });
    cams.forEach((c) => stopTracks(c.stream));
    stopTracks(micStream.current);
    if (timer.current) window.clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startMany(ids: string[]) {
    const ready = ids.filter((id) => cams.find((c) => c.deviceId === id)?.stream);
    if (!ready.length) return;
    const n = count;
    const all = [...new Set([...rolling, ...ready])];
    const carrier = soundCarrier(all, speaks);
    const fmt = takeFormat((m) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m));
    for (const id of ready) {
      if (recorders.current.has(id)) continue;
      const cam = cams.find((c) => c.deviceId === id)!;
      const source = recorderStream(cam.stream!, micStream.current, id === carrier);
      const rec = new MediaRecorder(source, fmt.mime ? { mimeType: fmt.mime, videoBitsPerSecond: 8_000_000 } : undefined);
      const entry = { rec, chunks: [] as Blob[] };
      rec.ondataavailable = (e) => { if (e.data.size) entry.chunks.push(e.data); };
      const name = takeNameFor({ label, camera: cameraName(cam, cams.indexOf(cam)), n, several: all.length > 1 });
      rec.onstop = () => {
        recorders.current.delete(id);
        void land(new Blob(entry.chunks, { type: fmt.mime || 'video/webm' }), fmt.ext, name);
      };
      recorders.current.set(id, entry);
      rec.start(1000);
    }
    if (!rolling.length) {
      startedAt.current = performance.now();
      setElapsed(0);
      timer.current = window.setInterval(() => setElapsed((performance.now() - startedAt.current) / 1000), 500);
    }
    setRolling(all);
    setCount(n + 1);
  }

  function stopOne(id: string) {
    const entry = recorders.current.get(id);
    if (entry && entry.rec.state !== 'inactive') entry.rec.stop();
    else recorders.current.delete(id);
    setRolling((r) => {
      const next = r.filter((x) => x !== id);
      if (!next.length && timer.current) { window.clearInterval(timer.current); timer.current = null; }
      return next;
    });
  }

  async function land(blob: Blob, ext: string, name: string) {
    setUploading((u) => u + 1);
    try {
      await uploadTake({ blob, ext, name, kind: 'camera', brandId: brand?.id ?? null });
      onCaptured();
    } catch (e) { onError((e as Error).message); }
    finally { setUploading((u) => u - 1); }
  }

  const live = cams.filter((c) => c.stream);
  const any = rolling.length > 0;

  return (
    <div className="panel" data-tool="camera-rig" style={{ marginBottom: 14 }}>
      <div className="panel-hd">
        <h3>Connected cameras</h3>
        <span className="row" style={{ gap: 6, alignItems: 'center' }}>
          {uploading > 0 && <span className="chip">uploading {uploading}</span>}
          {open && any && <span className="chip" style={{ background: '#dc2626', color: 'var(--on-accent)' }}>● REC {elapsed.toFixed(0)}s</span>}
          {!open
            ? <button className="btn pri" onClick={() => void openAll()}>Open the cameras</button>
            : <button className="btn" disabled={any} onClick={closeAll} title={any ? 'Stop the take first' : 'Turn the cameras off'}>Close</button>}
        </span>
      </div>
      <div className="panel-bd">
        {!open ? (
          <p className="hint" style={{ color: 'var(--ink-3)' }}>
            Every camera plugged into this machine, live, each one recordable on its own or all together.
            A take lands here as a camera source and can be dragged onto a scene of the timeline.
          </p>
        ) : (
          <>
            <div className="row wrap" style={{ gap: 10, alignItems: 'center', marginBottom: 10 }}>
              <input value={label} maxLength={80} disabled={any} onChange={(e) => setLabel(e.target.value)}
                     placeholder="Name the take — e.g. Interview, Bench demo; takes are numbered after it" style={{ flex: '1 1 240px' }} />
              <label className="sub-dim" title="One microphone, on one take: the scene that cuts to that take speaks; the other angles are picture only">Microphone{' '}
                <select value={mic} disabled={any} onChange={(e) => { setMic(e.target.value); void openMic(e.target.value); }}>
                  <option value="">default</option>
                  {mics.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'microphone'}</option>)}
                </select>
              </label>
              {live.length > 1 && (
                <label className="sub-dim" title="The camera whose take carries the sound">Sound on{' '}
                  <select value={speaks ?? ''} disabled={any} onChange={(e) => setSpeaks(e.target.value || null)}>
                    {live.map((c) => <option key={c.deviceId} value={c.deviceId}>{cameraName(c, cams.indexOf(c))}</option>)}
                  </select>
                </label>
              )}
              {live.length > 1 && (
                !any
                  ? <button className="btn pri" data-tool="capture-all" onClick={() => startMany(live.map((c) => c.deviceId))}>Capture all · take {count}</button>
                  : <button className="btn" style={{ background: 'var(--red)', color: 'var(--on-accent)' }} onClick={() => rolling.forEach(stopOne)}>Stop all</button>
              )}
            </div>
            {cams.length === 0 && <div className="empty">No camera was found. Plug one in and open the cameras again.</div>}
            <div className="rig">
              {cams.map((c, i) => {
                const on = rolling.includes(c.deviceId);
                const carries = any ? soundCarrier(rolling, speaks) === c.deviceId : soundCarrier(live.map((x) => x.deviceId), speaks) === c.deviceId;
                return (
                  <div key={c.deviceId} className={`rig-cam${on ? ' is-rolling' : ''}`}>
                    <div className="rig-pic">
                      <video ref={(el) => { if (el) videos.current.set(c.deviceId, el); else videos.current.delete(c.deviceId); }} muted playsInline />
                      {on && <span className="rig-rec">● REC</span>}
                      {c.error && <span className="rig-err">{c.error}</span>}
                    </div>
                    <div className="row" style={{ gap: 6, alignItems: 'center', justifyContent: 'space-between' }}>
                      <b style={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cameraName(c, i)}>{cameraName(c, i)}</b>
                      <span className="row" style={{ gap: 4, alignItems: 'center' }}>
                        {carries && c.stream && <span className="chip" title="This take carries the microphone">sound</span>}
                        {!on
                          ? <button className="btn tiny pri" disabled={!c.stream} onClick={() => startMany([c.deviceId])}>Capture</button>
                          : <button className="btn tiny" style={{ background: 'var(--red)', color: 'var(--on-accent)' }} onClick={() => stopOne(c.deviceId)}>Stop</button>}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="sub-dim" style={{ marginTop: 8 }}>
              Each take ingests for a minute or two, then shows in the library below as a camera source.
              On the Flight Deck it sits in the tray under the timeline — drag it onto a scene, and the scene cuts to it with its own sound.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
